import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import ApiService from './api';

class PushNotificationService {
  private static instance: PushNotificationService;
  private apiService: ApiService;
  private isInitialized: boolean = false;

  private constructor() {
    this.apiService = ApiService.getInstance();
  }

  public static getInstance(): PushNotificationService {
    if (!PushNotificationService.instance) {
      PushNotificationService.instance = new PushNotificationService();
    }
    return PushNotificationService.instance;
  }

  /**
   * Initialize push notifications for the app
   * Should be called after user login
   */
  public async initialize(): Promise<void> {
    // Only initialize on native platforms
    if (!Capacitor.isNativePlatform()) {
      console.log('📱 Push notifications only available on native platforms');
      return;
    }

    if (this.isInitialized) {
      console.log('📱 Push notifications already initialized');
      return;
    }

    try {
      console.log('📱 Initializing push notifications...');

      // Set up listeners BEFORE requesting permissions/registering
      // This ensures we don't miss any callbacks
      await PushNotifications.addListener('registration', async (token: Token) => {
        console.log('✅ Push registration success, token:', token.value);
        await this.registerDeviceToken(token.value);
      });

      // Handle registration errors
      await PushNotifications.addListener('registrationError', (error: any) => {
        console.error('❌ Push registration error:', error);
      });

      // Handle push notification received while app is in foreground
      await PushNotifications.addListener('pushNotificationReceived',
        (notification: PushNotificationSchema) => {
          console.log('📬 Push notification received:', notification);
          // You can show an in-app notification here
          this.handleNotificationReceived(notification);
        }
      );

      // Handle notification action (user tapped on notification)
      await PushNotifications.addListener('pushNotificationActionPerformed',
        (action: ActionPerformed) => {
          console.log('👆 Push notification action performed:', action);
          this.handleNotificationAction(action);
        }
      );

      // Now check permissions and register (listeners are already set up)
      const currentStatus = await PushNotifications.checkPermissions();
      console.log('📱 Current permission status:', currentStatus);

      // Request permission to use push notifications
      const permStatus = await PushNotifications.requestPermissions();
      console.log('📱 Permission request result:', permStatus);

      if (permStatus.receive === 'granted') {
        // Register with Apple / Google to receive push via APNS/FCM
        await PushNotifications.register();
        console.log('✅ Push notification registration initiated');
      } else if (permStatus.receive === 'denied') {
        console.warn('⚠️ Push notification permission denied - user needs to enable in Settings');
        // User previously denied, they need to go to Settings to enable
        return;
      } else {
        console.warn('⚠️ Push notification permission status:', permStatus.receive);
        return;
      }

      this.isInitialized = true;
      console.log('✅ Push notifications initialized successfully');

    } catch (error) {
      console.error('❌ Failed to initialize push notifications:', error);
    }
  }

  /**
   * Register device token with backend
   */
  private async registerDeviceToken(token: string): Promise<void> {
    try {
      const platform = Capacitor.getPlatform(); // 'ios' or 'android'
      const response = await this.apiService.registerDeviceToken(token, platform);

      if (response.success) {
        console.log('✅ Device token registered with backend');
      } else {
        console.error('❌ Failed to register device token:', response.error);
      }
    } catch (error) {
      console.error('❌ Error registering device token:', error);
    }
  }

  /**
   * Handle notification received while app is open
   */
  private handleNotificationReceived(notification: PushNotificationSchema): void {
    // Dispatch custom event that components can listen to
    const event = new CustomEvent('pushNotificationReceived', {
      detail: notification
    });
    window.dispatchEvent(event);
  }

  /**
   * Handle notification tap/action
   */
  private handleNotificationAction(action: ActionPerformed): void {
    const notification = action.notification;

    // Dispatch custom event that components can listen to for navigation
    const event = new CustomEvent('pushNotificationAction', {
      detail: {
        notification,
        actionId: action.actionId
      }
    });
    window.dispatchEvent(event);

    // You can add navigation logic here based on notification data
    // For example:
    // if (notification.data?.type === 'activity_invitation') {
    //   navigate to activity details
    // }
  }

  /**
   * Unregister from push notifications (e.g., on logout)
   */
  public async unregister(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await PushNotifications.removeAllListeners();
      this.isInitialized = false;
      console.log('✅ Push notifications unregistered');
    } catch (error) {
      console.error('❌ Error unregistering push notifications:', error);
    }
  }

  /**
   * Get list of delivered notifications
   */
  public async getDeliveredNotifications(): Promise<PushNotificationSchema[]> {
    if (!Capacitor.isNativePlatform()) {
      return [];
    }

    const notificationList = await PushNotifications.getDeliveredNotifications();
    return notificationList.notifications;
  }

  /**
   * Remove delivered notifications from notification center
   */
  public async removeDeliveredNotifications(notifications: PushNotificationSchema[]): Promise<void> {
    if (!Capacitor.isNativePlatform() || notifications.length === 0) {
      return;
    }

    await PushNotifications.removeDeliveredNotifications({
      notifications: notifications
    });
  }

  /**
   * Remove all delivered notifications
   */
  public async removeAllDeliveredNotifications(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    await PushNotifications.removeAllDeliveredNotifications();
  }
}

export default PushNotificationService;
